import type { City, Country } from '@atlasguess/engine';
import { usStateAbbreviation } from './usStates';

/**
 * Two-line prompt the player sees: the city on top (`primary`), the
 * country below it (`secondary`), with the country's flag inline next
 * to that second line. Splitting these out lets the Prompt component
 * style each piece independently — large serif primary, smaller dim
 * secondary, small flag-icon — without the formatter caring about
 * typography.
 *
 * Format rules:
 *   - default:    primary "Paris"        secondary "France"        flag FR
 *   - US cities:  primary "Dallas, TX"   secondary "United States" flag US
 *
 * `flagFile` is the raw path stored on the Country record (e.g.
 * "europe/FR.svg"); callers prefix the leading "/" when building the
 * <img> src. Returns null when the city's country isn't in the
 * dataset (defensive — should never happen in practice).
 *
 * Country resolution requires the country dataset; pass it in so this
 * stays a pure function.
 */
export interface CityPrompt {
  primary: string;
  secondary: string;
  flagFile: string | null;
}

export function buildCityPrompt(
  city: City,
  countriesByIso: Map<string, Country>,
): CityPrompt {
  const country = countriesByIso.get(city.country_iso);
  const countryName = country?.name ?? city.country_iso;
  const flagFile = country?.flag_file ?? null;

  // US gets the state appended to the primary line for disambiguation
  // (Houston, Springfield, etc. exist in many places). Use USPS abbr
  // to keep the line tight; fall back to the full subdivision name
  // if we don't recognize it (rare — covers NE quirks).
  if (city.country_iso === 'US' && city.subdivision) {
    const abbr = usStateAbbreviation(city.subdivision) ?? city.subdivision;
    return { primary: `${city.name}, ${abbr}`, secondary: countryName, flagFile };
  }
  return { primary: city.name, secondary: countryName, flagFile };
}
